import React, { useState, useRef } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import EditTextModal from "../EditTextModal/EditTextModal";
import TemplateImage from "../../assets/templates/template-1.png";
import imageCompression from "browser-image-compression";
import { useSnackbar } from "notistack";
import ImagePlaceholder from "../ImagePlaceholder/ImagePlaceholder";

const Template1 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const [loadingImage, setLoadingImage] = useState([
		false,
		false,
		false,
		false,
	]);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalText, setModalText] = useState("");
	const [selectedTextIndex, setSelectedTextIndex] = useState(null);

	const inputRefs = [useRef(null), useRef(null), useRef(null)];
	const cancelHandlerRefs = [useRef(null), useRef(null), useRef(null)];
	const processingRefs = [useRef(false), useRef(false), useRef(false)];

	const { enqueueSnackbar } = useSnackbar();
	const compressImage = async (file) => {
		const options = {
			maxSizeMB: 0.75, // (Max file size in MB)
			maxWidthOrHeight: 1280, // (Compressed file's maximum dimension in pixels)
			useWebWorker: true, // (Use a web worker to perform the compression in a background thread),
			maxIterations: 100,
		};

		try {
			const compressedFile = await imageCompression(file, options);
			console.log(compressedFile);
			return compressedFile; // This is the compressed image file
		} catch (error) {
			enqueueSnackbar(
				"La imagen no es compatible. Inténtalo con una distinta.",
				{ variant: "error" },
			);
			console.error(error);
			return null;
		}
	};

	const handleImageChange = async (event, index) => {
		// Marcar que estamos procesando un archivo
		processingRefs[index].current = true;

		// Limpiar el handler de cancelación si existe (usuario seleccionó un archivo)
		if (cancelHandlerRefs[index].current) {
			window.removeEventListener("focus", cancelHandlerRefs[index].current);
			cancelHandlerRefs[index].current = null;
		}

		// Manejar tanto eventos de input file como de drag and drop
		let rawFile;
		if (event.dataTransfer && event.dataTransfer.files) {
			// Es un evento de drag and drop
			rawFile = event.dataTransfer.files[0];
		} else if (event.target && event.target.files) {
			// Es un evento de input file
			rawFile = event.target.files[0];
		}

		if (!rawFile) {
			processingRefs[index].current = false;
			setLoadingImage((prev) => {
				let auxPrev = [...prev];
				auxPrev[index] = false;
				return auxPrev;
			});
			return;
		}

		// El loading ya está activo desde handleClickImageInput
		// Mantenerlo activo durante la compresión
		const file = await compressImage(rawFile);
		if (file) {
			try {
				const reader = new FileReader();
				reader.onload = () => {
					let auxPage = { ...page };
					auxPage.images[index] = {
						id: -new Date().getTime(),
						src: reader.result,
					};
					updatePage({ ...auxPage });
					// Desactivar loading cuando la imagen esté cargada
					processingRefs[index].current = false;
					setLoadingImage((prev) => {
						let auxPrev = [...prev];
						auxPrev[index] = false;
						return auxPrev;
					});
				};
				reader.onerror = () => {
					console.error("Error al leer la imagen");
					processingRefs[index].current = false;
					setLoadingImage((prev) => {
						let auxPrev = [...prev];
						auxPrev[index] = false;
						return auxPrev;
					});
				};
				reader.readAsDataURL(file);
			} catch (error) {
				console.error("Error al cargar la imagen:", error);
				processingRefs[index].current = false;
				setLoadingImage((prev) => {
					let auxPrev = [...prev];
					auxPrev[index] = false;
					return auxPrev;
				});
			}
		} else {
			// Si la compresión falla, desactivar loading
			processingRefs[index].current = false;
			setLoadingImage((prev) => {
				let auxPrev = [...prev];
				auxPrev[index] = false;
				return auxPrev;
			});
		}
	};

	const handleClickImageInput = (index) => {
		const inputElement = inputRefs[index].current;
		if (inputElement) {
			// Limpiar handler anterior si existe
			if (cancelHandlerRefs[index].current) {
				window.removeEventListener("focus", cancelHandlerRefs[index].current);
			}

			// Activar loading al hacer clic
			setLoadingImage((prev) => {
				let auxPrev = [...prev];
				auxPrev[index] = true;
				return auxPrev;
			});

			// Crear handler para detectar cuando el usuario vuelve a la ventana
			// Si no hay archivo seleccionado cuando vuelve, significa que canceló
			cancelHandlerRefs[index].current = () => {
				// Delay para asegurar que el onChange se haya procesado si seleccionó un archivo
				setTimeout(() => {
					// Si ya estamos procesando un archivo, no hacer nada
					if (processingRefs[index].current) {
						// Limpiar el handler pero no tocar el loading
						if (cancelHandlerRefs[index].current) {
							window.removeEventListener(
								"focus",
								cancelHandlerRefs[index].current,
							);
							cancelHandlerRefs[index].current = null;
						}
						return;
					}

					const hasFile = inputElement.files && inputElement.files.length > 0;
					if (!hasFile) {
						// No hay archivo y no estamos procesando, el usuario canceló - desactivar loading
						setLoadingImage((prev) => {
							if (prev[index]) {
								// Solo desactivar si aún está activo
								let auxPrev = [...prev];
								auxPrev[index] = false;
								return auxPrev;
							}
							return prev;
						});
					}
					// Limpiar el handler
					if (cancelHandlerRefs[index].current) {
						window.removeEventListener(
							"focus",
							cancelHandlerRefs[index].current,
						);
						cancelHandlerRefs[index].current = null;
					}
				}, 300); // Delay de 300ms para dar tiempo al onChange
			};

			// Agregar listener de focus al window (solo una vez)
			window.addEventListener("focus", cancelHandlerRefs[index].current, {
				once: true,
			});

			inputElement.click();
		}
	};

	const handleTextClick = (index) => {
		setSelectedTextIndex(index);
		setModalText(page.texts[index].content);
		setIsModalOpen(true);
	};

	const handleModalTextChange = (value) => {
		setModalText(value);
	};

	const handleModalSave = () => {
		let auxPage = { ...page };
		auxPage.texts[selectedTextIndex] = {
			content: modalText,
			isEditing: false,
		};
		updatePage({ ...auxPage });
		/*setTexts((prevTexts) => {
			const newTexts = [...prevTexts];
			newTexts[selectedTextIndex] = {
				content: modalText,
				isEditing: false,
			};
			return newTexts;
		});*/
		setIsModalOpen(false);
	};

	const handleModalCancel = () => {
		setIsModalOpen(false);
	};

	return (
		<Box
			sx={{
				display: "flex",
				height: "100%",
				width: "100%",
				flexDirection: "column",
				"& *:not(.upload-image-text)": {
					fontFamily: fontFamily + "!important",
				},
				backgroundColor: "#f1f2f6",
			}}
		>
			<Box
				data-block-id="header"
				onClick={() => handleTextClick(0)}
				sx={{
					height: "51px",
					minHeight: "51px",
					width: "100%",
					backgroundColor: headerBackgroundColor,
					position: "sticky",
					zIndex: 1053,
				}}
			>
				<Typography
					data-template-item="true"
					onClick={() => handleTextClick(0)}
					sx={{
						display: "flex!important",
						justifyContent: "center",
						height: "100%",
						alignItems: "center",
						color: headerTextColor,
						cursor: "pointer",
						fontSize: "14px!important",
						fontWeight: "bold!important",
					}}
					dangerouslySetInnerHTML={{
						__html: page.texts[0].content || "Título de la guía",
					}}
				/>
			</Box>

			<Box
				sx={{
					display: "flex",
					height: "100%",
					width: "100%",
					flex: 1,
				}}
			>
				<input
					type="file"
					accept="image/*"
					onChange={(e) => handleImageChange(e, 0)}
					style={{ display: "none" }}
					ref={inputRefs[0]}
				/>
				<label
					style={{
						width: "100%",
						height: "100%",
						display: "block",
						position: "relative",
					}}
					data-template-item={true}
					data-block-id="image-0"
					onDragOver={(e) => {
						// Permitir que el drag and drop funcione, pero no bloquear la propagación
						// para que llegue al ImagePlaceholder
						e.preventDefault();
						// NO usar stopPropagation para que el evento llegue al ImagePlaceholder
					}}
					onDrop={(e) => {
						// Prevenir el comportamiento por defecto del label, pero permitir propagación
						// Solo si NO es un drop en el ImagePlaceholder
						const isImagePlaceholder = e.target.closest(
							"[data-image-placeholder]",
						);
						if (!isImagePlaceholder) {
							e.preventDefault();
							// NO usar stopPropagation para que el evento pueda llegar al ImagePlaceholder
						}
					}}
				>
					{page.images[0] && page.images[0].src ? (
						<>
							{/*<div style={{ width: "100%", height: "100%" }}>
								<DraggableImage
									src={page.images[0]}
									alt="Descripción de la imagen"
								/>
					</div>*/}
							<div
								onClick={(e) => {
									e.stopPropagation();
									e.preventDefault();
									handleClickImageInput(0);
								}}
								onMouseDown={(e) => {
									e.stopPropagation();
								}}
								className="template-item"
								data-template-item
								style={{
									width: "100%",
									height: "100%",
									objectFit: "cover",
									backgroundImage: `url(${page.images[0].src})`,
									backgroundSize: "cover",
									backgroundPosition: "center",
									position: "relative",
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									padding: "30px",
									cursor: "pointer",
									zIndex: 1001,
									pointerEvents: "auto",
								}}
							>
								{/* Renderizar texts[1] si existe (para portadas con texto central) */}
								{/* El texto SVG se posiciona con marginTop negativo sobre la imagen central del canvas */}
								{/* La imagen central está en canvasImages en y=445, así que el texto debe estar en esa posición menos 55px */}
								{page.texts[1] && page.texts[1].content && (
									<Box
										sx={{
											position: "absolute",
											// Calcular posición: la imagen está en y=445 en el canvas (que empieza en y=60 del template)
											// El template tiene padding de 30px, así que la posición relativa es diferente
											// Aproximadamente: top = 50% - 55px (marginTop negativo del original)
											top: "calc(50% - 55px)",
											left: "50%",
											transform: "translateX(-50%)",
											zIndex: 102,
											width: "100%",
											display: "flex",
											justifyContent: "center",
											alignItems: "center",
											pointerEvents: "none", // Para que no interfiera con clics
										}}
										dangerouslySetInnerHTML={{ __html: page.texts[1].content }}
									/>
								)}
							</div>
						</>
					) : (
						!loadingImage[0] && (
							<ImagePlaceholder
								height="100%"
								onClick={(e) => {
									e.stopPropagation(); // Prevenir que el clic llegue al label
									handleClickImageInput(0);
								}}
								onDrop={(e) => {
									e.preventDefault();
									e.stopPropagation();
									setLoadingImage((prev) => {
										let auxPrev = [...prev];
										auxPrev[0] = true;
										return auxPrev;
									});
									handleImageChange(e, 0);
								}}
							/>
						)
					)}
					{loadingImage[0] && (
						<Box
							sx={{
								position: "absolute",
								top: 0,
								left: 0,
								width: "100%",
								height: "100%",
								backgroundColor: "rgba(248, 250, 252, 0.95)",
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								zIndex: 1000,
							}}
						>
							<CircularProgress size={40} />
						</Box>
					)}
				</label>
			</Box>
			<EditTextModal
				showModal={isModalOpen}
				handleClose={handleModalCancel}
				value={modalText}
				onChange={handleModalTextChange}
				onSave={handleModalSave}
			/>
		</Box>
	);
};

Template1.thumbnail = TemplateImage;

export default Template1;
